import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useSearchParams } from 'react-router-dom';
import { lightTheme } from '~/configs/leTheme';
import MUIProvider from '~/contexts/MUIProvider';
import NativeLensActions from '~/nativeLenses/NativeLensActions';
import { nativeLensId } from '~/nativeLenses/nativeLensId';
import { useNativeLensPrint } from '~/nativeLenses/useNativeLensPrint';

import { Alert, Box, CssBaseline, MenuItem, TextField, ThemeProvider } from '@mui/material';

import { UnifiedAnalyticsThemeProvider } from '~/pages/AskRichardUnified/UnifiedAnalyticsThemeProvider';
import PageChatLayout from '~/pages/AskRichardUnified/page/PageChatLayout';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';
import type { PageField } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';
import type { LensPrintTarget } from '~/pages/LeLens/print/lensPrintTypes';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import type { LensFilterValues } from '~/types/leLens';

import { lensQueriedAtLabel } from '~/utils/lensQueriedAt';

import { luxPlusMuiControls } from './LuxPlusMuiControls';
import LuxPlusWorkbook from './LuxPlusWorkbook';
import { breakdownSelection, linkedSelection, toggleLinkedSelection } from './controls';
import { readFidelityFilters, writeFidelityFilters } from './fidelityQuery';
import {
  countryLabel,
  defaultScope,
  getLiveFilterOptions,
  requestLux,
  scopeBindings,
  scopeProblem,
} from './filterBindings';
import {
  AUTO_RENEW_ON_CARD,
  activeCards,
  adaptLux,
  autoRenewOnRate,
  luxCsvFileName,
  luxPanelCsv,
  panelForView,
} from './liveDataAdapter';
import { FIDELITY_VIEWS, type FidelityCadence, type FidelityFilters, type FidelityViewId } from './presentationModel';
import config from './reportConfig.json';

type PrintProps = { printTarget?: LensPrintTarget; printFilters?: Record<string, unknown> };

function WorkbookBody({ printTarget, printFilters }: PrintProps) {
  const root = useRef<HTMLDivElement>(null);
  const [params, setParams] = useSearchParams(),
    [clock] = useState(() => printTarget?.generatedAt ?? new Date());
  const view =
    FIDELITY_VIEWS.find((v) => v.id === (params.get('lpView') ?? params.get('reportView')))?.id ?? 'overview';
  const read = readFidelityFilters(params, 'lp');
  // Preserve both URL namespaces. Each visible edit removes its older alias.
  const rawFilters = {
    ...read,
    startDate: params.get('lpFrom') ?? params.get(view === 'cohorts' ? 'cohort_from' : 'from') ?? '',
    endDate: params.get('lpTo') ?? params.get(view === 'cohorts' ? 'cohort_to' : 'to') ?? '',
  };
  const filterKey = JSON.stringify(rawFilters),
    filters = useMemo(() => JSON.parse(filterKey) as FidelityFilters, [filterKey]);
  const grain = params.get('lpCadence') ?? params.get('grain') ?? 'week';
  const cadence: FidelityCadence = ['day', 'week', 'month'].includes(grain) ? (grain as FidelityCadence) : 'week';
  const split = params.get('lpSplit') === 'all' ? undefined : 'market';
  const layout = params.get('lpLayout') === 'baseline' ? 'baseline' : 'workbook';
  const panel = panelForView(view);
  const country =
    panel === 'executive' ? 'executive_country' : panel === 'renewals' ? 'parent_membership_country' : 'country_code_2';
  const [countryDomains, setCountryDomains] = useState<Record<string, Array<string>>>({});
  const countryDomain = countryDomains[country] ?? null;
  const requestedTheme = params.get('reportTheme') ?? params.get('lpTheme');
  const reportTheme = requestedTheme === 'dark' ? 'dark' : 'light';
  const scope = useMemo(
    () => requestLux(filters, view, cadence, params, clock, countryDomain),
    [filters, view, cadence, params, clock, countryDomain],
  );
  const constraint =
    scopeProblem(filters, view, scope) ??
    (!['day', 'week', 'month'].includes(grain)
      ? 'This native cadence has no preserved workbook projection. Choose Daily, Weekly or Monthly; values are withheld.'
      : params.has('time_split') && params.get('time_split') !== 'cadence'
        ? 'This native table time split needs the I00 projection repair; values are withheld.'
        : null);
  const query = useGetReportingLeLensData(
    !constraint ? nativeLensId('luxplus') : undefined,
    printFilters ? (printFilters as LensFilterValues) : { ...scope, native_view: view },
    true,
    undefined,
    undefined,
    undefined,
    activeCards(view),
    printTarget ? 'json' : 'stream',
  );
  // Separate optional request: if the backend doesn't serve this card yet, only this query fails and the tile
  // falls back to the renewals summary.
  const autoRenewQuery = useGetReportingLeLensData(
    !constraint && view === 'renewals' ? nativeLensId('luxplus') : undefined,
    scope,
    false,
    undefined,
    undefined,
    undefined,
    [AUTO_RENEW_ON_CARD],
  );
  const autoRenewOn = autoRenewQuery.isError ? null : autoRenewOnRate(autoRenewQuery.data?.cards);
  const liveCountryDomain = query.data?.filterOptions?.[country];
  const domainKey = JSON.stringify(liveCountryDomain);
  useEffect(() => {
    if (liveCountryDomain && JSON.stringify(countryDomain) !== domainKey) {
      setCountryDomains((previous) => ({ ...previous, [country]: liveCountryDomain }));
    }
  }, [country, liveCountryDomain, domainKey, countryDomain]);
  const domainReady =
    filters.market === 'All markets' || (countryDomain !== null && JSON.stringify(countryDomain) === domainKey);
  const modelConstraint = constraint ?? (!domainReady ? 'Waiting for live country members for this selection.' : null);
  const settled = useMemo(
    () =>
      adaptLux(
        filters,
        view,
        scope,
        {
          requestIdentity: query.requestIdentity,
          responseIdentity: query.responseIdentity,
          isSuccess: query.isSuccess,
          isFetching: query.isFetching,
          isError: query.isError,
          isShowingPreviousData: query.isShowingPreviousData,
          cards: query.data?.cards,
        },
        split === 'market',
        modelConstraint,
        autoRenewOn,
      ),
    [
      autoRenewOn,
      filters,
      view,
      scope,
      query.requestIdentity,
      query.responseIdentity,
      query.isSuccess,
      query.isFetching,
      query.isError,
      query.isShowingPreviousData,
      query.data?.cards,
      split,
      modelConstraint,
    ],
  );
  const ready = settled.ready && !constraint,
    problem = constraint ?? query.error?.message ?? settled.problem;
  const { printing, downloadPdf } = useNativeLensPrint({
    root,
    ready: Boolean(ready),
    error: query.isError || constraint || (query.isSuccess && !query.isFetching && !ready) ? problem : null,
    target: printTarget,
    title: settled.board.title,
    expectedCardCount: settled.board.panels.length,
  });
  const sourceOptions = query.data?.filterOptions ?? {};
  const cohortMonths = [...new Set((settled.cards.workbook_cohort_cells ?? []).map((r) => String(r.date)))].sort();
  if (filters.enrollmentMonth && filters.enrollmentMonth !== 'All' && !cohortMonths.includes(filters.enrollmentMonth)) {
    cohortMonths.push(filters.enrollmentMonth);
  }
  const options = getLiveFilterOptions(view, filters, sourceOptions, cohortMonths);
  const dateWindow = scope.period.window;
  const model = {
    markets: [...new Set((sourceOptions[country] ?? []).map(countryLabel))],
    range: { min: dateWindow.from, max: dateWindow.to },
  };
  function replace(next: URLSearchParams) {
    setParams(next, { replace: true });
  }
  function nativeChanges(values: Record<string, string>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(values)) {
      next.set(key, value);
    }
    replace(next);
  }
  function change(values: Partial<FidelityFilters> & { view?: FidelityViewId }) {
    const { view: nextView, ...scopeChanges } = values;
    let next = new URLSearchParams(params);
    if (scopeChanges.startDate === '' && scopeChanges.endDate === '' && scopeChanges.market === 'All markets') {
      for (const f of config.filters) {
        next.delete(f.id);
      }
      for (const p of config.parameters) {
        next.delete(p.id);
      }
      for (const key of ['data_split', 'grain', 'from', 'to', 'cohort_from', 'cohort_to']) {
        next.delete(key);
      }
      next.set('lpCadence', 'week');
      next.set('lpSplit', 'market');
    }
    if ('enrollmentMonth' in scopeChanges) {
      next.delete('cohort_from');
      next.delete('cohort_to');
    }
    if ('startDate' in scopeChanges) {
      next.delete('from');
      next.delete('cohort_from');
    }
    if ('endDate' in scopeChanges) {
      next.delete('to');
      next.delete('cohort_to');
    }
    if ('market' in scopeChanges) {
      next.delete(country);
    }
    for (const key of Object.keys(scopeChanges)) {
      if (scopeBindings[key]) {
        next.delete(scopeBindings[key]);
      }
    }
    next = writeFidelityFilters(next, scopeChanges, 'lp');
    if (nextView) {
      next.set('lpView', nextView);
      next.delete('reportView');
    }
    replace(next);
  }
  const schema: Record<string, PageField> = {
    view: { type: 'enum', values: FIDELITY_VIEWS.map((v) => v.id) },
    from: { type: 'date' },
    to: { type: 'date' },
    market: { type: 'enum', values: ['All markets', ...model.markets] },
    cadence: { type: 'enum', values: ['day', 'week', 'month'] },
    split: { type: 'enum', values: ['market', 'all'] },
  };
  for (const option of options) {
    schema[option.key] = { type: 'enum', values: option.options };
  }
  usePageBridge({
    pageId: 'luxplus',
    view: {
      title: settled.board.title,
      ready,
      filters: {
        ...defaultScope(filters, view),
        view,
        cadence,
        split: split ?? 'all',
        from: dateWindow.from,
        to: dateWindow.to,
      },
      filterSchema: schema,
      effectiveRequest: scope,
      sourceAsOf: null,
      metrics: Object.fromEntries(
        settled.board.panels.flatMap((p) => (p.metrics ?? []).map((m) => [p.id + '.' + m.key, m.value])),
      ),
      visibleSections: settled.board.panels.map((p) => ({
        id: p.id,
        title: p.title,
        rowCount: p.rows.length,
        rows: p.rows.slice(0, 10),
        sample: p.rows.length > 10,
      })),
      selection: { linked: scope.lux_action, breakdown: scope.lux_breakdown_action },
    },
    edit: (changes) => {
      if (!ready) {
        throw new Error('LuxPlus data is not ready.');
      }
      validatePageFilters(changes, schema);
      const next = new URLSearchParams(params),
        oldChanges: Partial<FidelityFilters> = {};
      for (const [key, value] of Object.entries(changes)) {
        if (key === 'view') {
          next.set('lpView', String(value));
          next.delete('reportView');
        } else if (key === 'cadence') {
          next.set('lpCadence', String(value));
          next.delete('grain');
        } else if (key === 'split') {
          next.set('lpSplit', String(value));
        } else {
          const k = key === 'from' ? 'startDate' : key === 'to' ? 'endDate' : key;
          if (view === 'cohorts' && (key === 'from' || key === 'to')) {
            next.set('cohort_' + key, String(value));
            continue;
          }
          oldChanges[k] = String(value);
          if (key === 'from' || key === 'to') {
            next.delete(key);
            next.delete('cohort_' + key);
          }
          if (scopeBindings[k]) {
            next.delete(scopeBindings[k]);
          }
          if (key === 'market') {
            next.delete(country);
          }
        }
      }
      replace(writeFidelityFilters(next, oldChanges, 'lp'));
    },
  });
  const dimensions = config.panels.find((p) => p.id === panel)?.dimensions ?? [];
  const selectedSplits = [
    String(scope.data_split ?? 'All'),
    String(scope.data_split_2 ?? 'All'),
    String(scope.data_split_3 ?? 'All'),
  ];
  const extraControls = (
    <details className="lp-gap-details">
      <summary>Source filters & actions</summary>
      {panel === 'opportunities' &&
        [0, 1, 2].map((i) => (
          <label key={i}>
            Data split {i + 1}
            <select
              aria-label={'Data split ' + (i + 1)}
              value={selectedSplits[i]}
              onChange={(e) =>
                nativeChanges({
                  [i ? 'data_split_' + (i + 1) : 'data_split']: e.target.value,
                  lux_action: '{}',
                  lux_breakdown_action: '{}',
                })
              }
            >
              {['All', ...dimensions].map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>
          </label>
        ))}
      {config.parameters
        .filter((p) => p.panels.includes(panel) && !p.id.startsWith('lux_') && !p.id.startsWith('data_split_'))
        .map((p) => (
          <label key={p.id}>
            {p.label}
            <select
              aria-label={p.label}
              value={String(scope[p.id])}
              onChange={(e) => nativeChanges({ [p.id]: e.target.value, lux_action: '{}' })}
            >
              {p.options.map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>
          </label>
        ))}
      {config.filters
        .filter((f) => dimensions.includes(f.id))
        .map((f) => (
          <TextField
            key={f.id}
            select
            label={f.label}
            value={params.getAll(f.id)}
            size="small"
            fullWidth
            slotProps={{ select: { multiple: true } }}
            onChange={(e) => {
              const next = new URLSearchParams(params),
                values = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
              next.delete(f.id);
              values.forEach((v) => next.append(f.id, v));
              for (const [key, binding] of Object.entries(scopeBindings)) {
                if (binding === f.id) {
                  next.delete('lp' + key[0].toUpperCase() + key.slice(1));
                }
              }
              if (f.id === country) {
                next.delete('lpMarket');
              }
              replace(next);
            }}
          >
            {[...new Set([...(sourceOptions[f.id] ?? []), ...params.getAll(f.id)])].map((v) => (
              <MenuItem key={v} value={v}>
                {v || '(blank)'}
              </MenuItem>
            ))}
          </TextField>
        ))}
      {panel === 'opportunities' && selectedSplits[2] !== 'All' && (
        <TextField
          select
          label="TUR breakdown selection"
          disabled={!ready}
          fullWidth
          size="small"
          value={(() => {
            try {
              const x = JSON.parse(String(scope.lux_breakdown_action));
              return Array.isArray(x.values?.[0]) ? x.values[0] : x.values?.[0] ? [String(x.values[0])] : [];
            } catch {
              return [];
            }
          })()}
          slotProps={{ select: { multiple: true } }}
          onChange={(e) =>
            nativeChanges({
              lux_breakdown_action: breakdownSelection(
                selectedSplits[2],
                Array.isArray(e.target.value) ? e.target.value : [e.target.value],
              ),
            })
          }
        >
          {[
            ...new Set(
              (ready ? (settled.cards.opportunities_rows ?? []) : []).map((r) => String(r.split_3 ?? 'Unknown')),
            ),
          ].map((v) => (
            <MenuItem key={v} value={v}>
              {v}
            </MenuItem>
          ))}
        </TextField>
      )}
      <button type="button" onClick={() => nativeChanges({ lux_action: '{}', lux_breakdown_action: '{}' })}>
        Clear linked selections
      </button>
    </details>
  );
  return (
    <Box ref={root} sx={{ minWidth: 0, color: 'text.primary' }}>
      {query.isError && <Alert severity="error">{problem}</Alert>}
      <LuxPlusWorkbook
        board={settled.board}
        printing={printing}
        reportActions={
          <NativeLensActions
            pageId="luxplus"
            filters={{ ...scope, native_view: view }}
            filterOptions={sourceOptions}
            initialPeriod={filters.startDate || filters.endDate ? scope.period : null}
            ready={Boolean(ready && !printing)}
            onDownloadPdf={downloadPdf}
          />
        }
        model={model}
        filterOptions={options}
        ready={ready}
        problem={problem}
        filters={filters}
        view={view}
        cadence={cadence}
        onCadence={(value) => {
          const next = new URLSearchParams(params);
          next.set('lpCadence', value);
          next.delete('grain');
          replace(next);
        }}
        split={split}
        queriedAt={lensQueriedAtLabel(query.data?.cards)}
        onSplit={(value) => nativeChanges({ lpSplit: value ?? 'all' })}
        theme={printing ? 'light' : reportTheme}
        onTheme={() =>
          nativeChanges({
            lpTheme: reportTheme === 'dark' ? 'light' : 'dark',
            reportTheme: reportTheme === 'dark' ? 'light' : 'dark',
          })
        }
        onChange={change}
        layout={layout}
        onLayout={(value) => nativeChanges({ lpLayout: value })}
        controls={luxPlusMuiControls}
        nativeControls
        nativeFlow
        extraControls={extraControls}
        onSourceMark={(row, keys) => {
          if (!ready) {
            return;
          }
          const actualKeys = keys.map((key) =>
            key === 'split'
              ? selectedSplits[0]
              : key === 'split_2'
                ? selectedSplits[1]
                : key === 'split_3'
                  ? selectedSplits[2]
                  : key,
          );
          const mark = linkedSelection(
            panel,
            actualKeys,
            keys.map((k) => row[k]),
          );
          nativeChanges({ lux_action: toggleLinkedSelection(String(scope.lux_action ?? '{}'), mark) });
        }}
        exportPanel={(p) => {
          const contents = luxPanelCsv({ ...settled, ready }, p, settled.identity),
            href = URL.createObjectURL(new Blob([contents], { type: 'text/csv;charset=utf-8;' })),
            anchor = document.createElement('a');
          anchor.href = href;
          anchor.download = luxCsvFileName(settled.filters, view, p);
          anchor.click();
          window.setTimeout(() => URL.revokeObjectURL(href), 1000);
        }}
      />
    </Box>
  );
}
export default function LuxPlusWorkbookBody({ printTarget, printFilters }: PrintProps) {
  if (printTarget) {
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <UnifiedAnalyticsThemeProvider>
          <WorkbookBody printTarget={printTarget} printFilters={printFilters} />
        </UnifiedAnalyticsThemeProvider>
      </ThemeProvider>
    );
  }
  return (
    <MUIProvider>
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <UnifiedAnalyticsThemeProvider>
          <PageChatLayout>
            <WorkbookBody />
          </PageChatLayout>
        </UnifiedAnalyticsThemeProvider>
      </ThemeProvider>
    </MUIProvider>
  );
}
